import { BedDouble, Coffee, Phone, MapPin } from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { PageHero } from "@/components/layout/PageHero";
import { SectionHead, Reveal } from "@/components/layout/Motion";
import { TextLink } from "@/components/layout/CallButton";
import { CallCta, PaymentOptions } from "@/components/layout/CtaBlocks";
import { HOTEL_REGIONS } from "@/data/content";
import { PHONE_TEL } from "@/lib/site";
import { useCurrency } from "@/context/CurrencyContext";

const HotelCard = ({ h, i }) => {
  const { format } = useCurrency();
  return (
    <Reveal delay={i * 0.06} className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-shadow hover:shadow-xl" data-testid={`hotel-card-${h.name.slice(0, 12).replace(/\W/g, "")}`}>
      <div className="relative aspect-[3/2] overflow-hidden">
        <img src={h.image} alt={h.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <span className="absolute left-3 top-3 flex items-center gap-1 rounded-md bg-white px-2 py-1 text-[11px] font-bold text-ink"><MapPin className="h-3 w-3" />{h.area}</span>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-display text-base font-bold text-ink">{h.name}</h3>
        <p className="mt-2 text-xs leading-relaxed text-slate-600">{h.desc}</p>
        <div className="mt-auto flex items-end justify-between border-t border-slate-100 pt-4">
          <div><p className="text-[10px] text-slate-500">Indicative from</p><p className="font-display text-xl font-extrabold text-ink">{format(h.price)} <span className="text-xs font-normal text-slate-500">/ night</span></p></div>
          <a href={PHONE_TEL} className="flex items-center gap-1.5 rounded-lg bg-brand-soft px-3 py-2 text-xs font-bold text-brand hover:bg-brand hover:text-white"><Phone className="h-3.5 w-3.5" />Check rate</a>
        </div>
      </div>
    </Reveal>
  );
};

export default function Hotels() {
  return (
    <Layout>
      <PageHero testId="hotels-hero" eyebrow="Reserve Cheap Flights stays · A world of possibilities" icon={BedDouble} image="/img/hero-hotels.jpg"
        line1="A good trip." line2="An even better stay." text="City mornings. Slow weekends. A room that feels just right. Find your next stay anywhere in the world, with a little human help."
        secondary={{ href: "#stays", label: "Explore stays" }} />
      <div className="border-b border-slate-100 bg-white">
        <div className="mx-auto grid max-w-[1320px] gap-3 px-4 py-4 text-sm text-slate-600 sm:grid-cols-3 sm:px-8">
          <p className="flex items-center gap-2"><BedDouble className="h-4 w-4 text-brand" />City stays & memorable escapes</p>
          <p className="flex items-center gap-2 sm:justify-center"><Coffee className="h-4 w-4 text-brand" />A stay that suits your plans</p>
          <p className="flex items-center gap-2 sm:justify-end"><Phone className="h-4 w-4 text-brand" />Personal help, just a call away</p>
        </div>
      </div>
      <section id="stays" className="bg-sand py-16">
        <div className="mx-auto max-w-[1320px] px-4 sm:px-8">
          <SectionHead eyebrow="Check in to something good" title="Great stays, around the world" text="Real properties. Indicative nightly rates. The details, confirmed with you." action={<TextLink testId="find-stay-link">Let’s find your stay</TextLink>} />
          {HOTEL_REGIONS.map((r) => (
            <div key={r.title} className="mt-12 border-t border-slate-200 pt-10 first:border-0" data-testid={`hotel-region-${r.title.split(" ")[0].toLowerCase()}`}>
              <h3 className="font-display text-xl font-bold text-ink">{r.title}</h3>
              <p className="mt-1 text-sm text-slate-500">{r.text}</p>
              <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{r.hotels.map((h, i) => <HotelCard key={h.name} h={h} i={i} />)}</div>
            </div>
          ))}
          <p className="mt-10 text-center text-[11px] text-slate-500">Rates are indicative and subject to availability. Currency conversions are indicative.</p>
        </div>
      </section>
      <CallCta eyebrow="Make room for your next adventure" title={["Tell us what a great stay", "looks like."]} text="The right location, the right room, and a real person to help you plan it." />
      <PaymentOptions />
    </Layout>
  );
}
